<template>

    <header
        :class="isSticky === true ? 'fixed top-0 left-0 z-30 w-full mb-5 sm:mb-8 border-t border-b border-slate-100 shadow-xs bg-white' : 'relative z-30 mb-5 sm:mb-8 border-t border-b border-slate-100 bg-white'">
        <div class="container py-3.5 px-4 lg:py-0">
            <div class="flex items-center justify-between gap-5">
                <!--  Mobile Responsive Start -->
                <button type="button" class="leading-none block lg:hidden"
                    @click.prevent="showTarget('mobile-sidebar-canvas', 'canvas-active')">
                    <i class="lab-line-humburger text-xl"></i>
                </button>
                <!--  Logo -->
                <div class="z-50">
                    <router-link :to="{ name: 'frontend.home' }"
                        class="router-link-active router-link-exact-active flex-shrink-0 brand-logo">
                        <img class="logo" :src="setting.theme_logo" alt="logo">
                    </router-link>
                </div>

                <button type="button" class="leading-none block lg:hidden"
                    @click.prevent="showTarget('search', 'search-active')">
                    <i class="lab-line-search text-xl"></i>
                </button>
                <!--  Logo & Mobile Responsive End -->

                <!-- MenuBar Start -->
                <nav class="header-nav hidden lg:block">
                    <ul class="header-nav-list">
                        <li class="header-nav-item">
                            <router-link class="header-nav-menu"
                                :class="checkIsPathAndRoutePathSame('/home') ? 'router-link-active router-link-exact-active' : ''"
                                :to="{ name: 'frontend.home' }">
                                {{ $t("label.home") }}
                            </router-link>
                        </li>

                        <li class="header-nav-item" ref="categoryNavItem" @mouseleave="closeCategoryMenu">
                            <button type="button" class="header-nav-menu down-arrow" @mouseenter="openCategoryMenu">
                                {{ $t('label.categories') }}
                            </button>
                            <div
                                class="header-cat-dropdown fixed top-[64px] left-0 z-10 w-full"
                                :class="{ 'cat-menu-open': isCategoryOpen }">
                                <div class="container">
                                    <div class="w-full rounded-b-2xl shadow-lg overflow-hidden bg-white border-t border-gray-200">
                                        <nav class="w-full overflow-hidden relative group" @mouseleave="stopMarqueeScroll">
                                            <button type="button"
                                                class="nav-cat-fade-btn fade-left"
                                                @mouseenter="startMarqueeScroll('left')" @mouseleave="stopMarqueeScroll"
                                                @mousedown.prevent="startMarqueeScroll('left')" @mouseup="stopMarqueeScroll"
                                                @click.prevent="scrollCategoryTabs('left')">
                                                <i class="lab-line-chevron-left text-lg"></i>
                                            </button>

                                            <div ref="categoryTabsTrack" 
                                                class="nav-cat-marquee-track w-full overflow-hidden flex items-center justify-start px-8">
                                                <button v-for="(category, index) in categories" :key="index"
                                                    type="button"
                                                    @click.prevent="selectCategoryTab(category)"
                                                    class="nowrap capitalize text-sm font-semibold tracking-wide px-5 py-4 transition-all duration-300 relative before:content-[''] before:absolute before:bottom-0 before:left-0 before:h-0.5 before:bg-primary hover:text-primary cursor-pointer"
                                                    :class="{ 'text-primary before:w-full before:transition-all before:duration-300': activeTab === 'category_' + category.slug }">
                                                    {{ category.name }}
                                                </button>
                                            </div>

                                            <button type="button"
                                                class="nav-cat-fade-btn fade-right"
                                                @mouseenter="startMarqueeScroll('right')" @mouseleave="stopMarqueeScroll"
                                                @mousedown.prevent="startMarqueeScroll('right')" @mouseup="stopMarqueeScroll"
                                                @click.prevent="scrollCategoryTabs('right')">
                                                <i class="lab-line-chevron-right text-lg"></i>
                                            </button>
                                        </nav>
                                        <transition name="sub-cat-slide" mode="out-in">
                                            <div :key="activeCategory ? activeCategory.slug : 'empty'">
                                                <div v-if="activeCategory && activeCategory.children && activeCategory.children.length > 0"
                                                    class="flex items-start gap-5 pb-5 border-t border-gray-200">
                                                    <div class="w-60 h-80 flex-shrink-0 pt-5 ltr:pl-5 rtl:pr-5">
                                                        <img class="w-full h-full object-top object-cover rounded-lg"
                                                            :src="activeCategory.cover" alt="category" />
                                                    </div>
                                                    <div class="w-full h-80 thin-scrolling pt-5 ltr:pr-5 rtl:pl-5">
                                                        <div class="w-full grid gap-5 grid-cols-3">
                                                            <div v-for="children in activeCategory.children" :key="children.id || children.slug" class="self-start">
                                                                <h3
                                                                    class="text-sm font-semibold capitalize pb-3 border-b border-slate-200">
                                                                    <router-link
                                                                        :to="{ name: 'frontend.product', query: { category: children.slug } }"
                                                                        @click="closeCategoryMenu"
                                                                        class="hover:text-primary transition-all duration-300">
                                                                        {{ children.name }}
                                                                    </router-link>
                                                                </h3>

                                                                <nav v-if="children.children && children.children.length > 0"
                                                                    class="flex flex-col mt-2">
                                                                    <MenuChildrenComponent
                                                                        :categories="children.children" />
                                                                </nav>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                </div>
                            </div>
                        </li>

                        <li class="header-nav-item">
                            <router-link class="header-nav-menu"
                                :class="checkIsPathAndRoutePathSame('/offers') ? 'router-link-active router-link-exact-active' : ''"
                                :to="{ name: 'frontend.offers' }">
                                {{ $t("label.offers") }}
                            </router-link>
                        </li>
                    </ul>
                    <div ref="pwaModal" v-if="!isPwaViewed" class="pwaModal modal active ff-modal">
                        <div class="modal-dialog max-w-[360px] p-6 text-center relative">
                            <button class="modal-close absolute top-4 right-4" @click.prevent="closePwaModal">
                                <i class="fa-solid fa-xmark text-text"></i>
                            </button>
                            <h3 class="text-[18px] font-semibold leading-5 mb-2">
                                {{ $t("label.install_app") }}
                            </h3>
                            <p class="text-sm font-normal leading-5 mb-5">
                                {{ $t("label.use_install_app") }}
                            </p>
                            <div class="flex gap-3 justify-center text-center border-t pt-5">
                                <button type="button" class=" modal-close modal-btn-outline "
                                    @click.prevent="closePwaModal">
                                    <i class="lab lab-fill-close-circle"></i>
                                    <span>{{ $t("button.close") }}</span>
                                </button>
                                <button id="installPWA" class="db-btn py-2 text-white bg-primary"
                                    @click.prevent="closePwaModal">
                                    <i class="lab lab-fill-save"></i><span> {{
                                        $t('button.install') }}</span></button>
                            </div>
                        </div>
                    </div>
                </nav>
                <!-- MenuBar End -->

                <!-- Mobile Search Start -->
                <form @submit.prevent="search"
                    class="hidden w-full lg:w-80 h-10 rounded-3xl lg:flex items-center gap-2 px-4 border  bg-gray-100 transition-all duration-300 focus-within:border-primary focus-within:bg-white">
                    <button class="lab-line-search text-lg flex-shrink-0"></button>
                    <input v-model="searchProduct" class="w-full h-full !ring-0 ring-0 focus:ring-0 focus-visible:ring-0 !outline-none outline-none focus:outline-none focus-visible:outline-none" type="search"
                        :placeholder="$t('label.search') + '...'" />
                </form>
                <!-- Mobile Search Start -->

                <!-- Language Start -->
                <div v-if="setting.site_language_switch === enums.activityEnum.ENABLE"
                    class="relative group hidden lg:block">
                    <button type="button" class="flex items-center gap-1.5 py-5 down-arrow" :title="language ? language.name : 'Idioma'">
                        <img :src="language.image" alt="language" class="w-5 h-5 rounded-full object-cover shadow-xs border border-slate-100" />
                    </button>

                    <ul
                        class="nav-dropdown-smooth w-40 absolute top-14 ltr:left-0 rtl:right-0 shadow-paper rounded-lg z-30 p-2 bg-white border border-gray-100">
                        <li v-for="(LoopLanguage, index) in languages" :key="index"
                            @click.prevent="changeLanguage(LoopLanguage.id, LoopLanguage.code, LoopLanguage.display_mode)"
                            class="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg relative w-full cursor-pointer transition-all duration-300 hover:bg-slate-100"
                            :class="{ 'bg-slate-100 font-bold': language && language.id === LoopLanguage.id }">
                            <img :src="LoopLanguage.image" alt="flags" class="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                            <span class="text-sm font-medium capitalize flex-auto">{{ LoopLanguage.name }}</span>
                        </li>
                    </ul>
                </div>
                <!-- Language End -->

                <!-- Currency Switcher Start -->
                <div v-if="setting.site_currency_switch === enums.activityEnum.ENABLE && activeCurrencies.length > 1"
                    class="relative group hidden lg:block">
                    <button type="button" class="flex items-center py-5 down-arrow">
                        <span class="font-bold text-sm text-primary">{{ selectedCurrency ? selectedCurrency.symbol : '$' }}</span>
                        <span class="font-bold uppercase text-sm font-lead">{{ selectedCurrency ? selectedCurrency.code : 'MXN' }}</span>
                    </button>

                    <ul
                        class="nav-dropdown-smooth w-auto absolute top-14 ltr:left-0 rtl:right-0 shadow-paper rounded-lg z-30 p-2 bg-white border border-gray-100">
                        <li v-for="curr in activeCurrencies" :key="curr.id"
                            @click.prevent="changeCurrency(curr)"
                            class="flex items-center gap-0 px-2.5 py-1.5 rounded-lg relative w-full cursor-pointer transition-all duration-300 hover:bg-slate-100"
                            :class="{ 'bg-slate-100 font-bold': selectedCurrency && selectedCurrency.id === curr.id }">
                            <span class="font-bold text-xs text-primary w-4 text-center">{{ curr.symbol }}</span>
                            <span class="text-xs font-semibold nowrap flex-auto">{{ curr.code }} / {{ curr.name }}</span>
                        </li>
                    </ul>
                </div>
                <!-- Currency Switcher End -->

                <div class="hidden flex items-center justify-between gap-2 lg:flex">

                    <!-- Wishlist Start -->
                    <router-link class="hidden lg:block relative" :to="{ name: 'frontend.wishlist' }">
                        <div class="relative lg:block group leading-1 py-3 px-3 text-primary hover:opacity-50 rounded-md">
                            <i class="fa-regular fa-heart text-xl"></i>
                            <span v-if="wishlists.length > 0"
                                class="absolute bottom-3.5 left-5.5 text-[10px] py-0.5 px-1 leading-1 text-center rounded-md bg-primary text-white font-bold">
                                {{ wishlists.length }}
                            </span>
                        </div>
                    </router-link>
                    <!-- WishList End -->


                    <!-- My Account Start -->
                    <div class="relative hidden pr-2 lg:block group" @mouseleave="isUserDropdownClosed = false">
                        <button type="button" class="fa-solid fa-circle-user text-xl py-4 px-3 text-primary hover:opacity-50 rounded-md"></button>
                        <div class="nav-dropdown-smooth w-60 absolute top-12 ltr:-right-2 rtl:-left-2 z-30 rounded-xl border border-gray-100 overflow-hidden shadow-paper bg-white" :class="{ 'force-closed': isUserDropdownClosed }">
                            <div v-if="logged">
                                <div class="flex items-center gap-3 p-4 border-b">
                                    <img :src="profile.image" alt="avatar"
                                        class="w-11 h-11 rounded-full object-cover flex-shrink-0">
                                    <dl class="w-full">
                                        <dt class="font-semibold capitalize whitespace-nowrap mb-0.5">
                                            {{ textShortener(profile.name, 20) }}
                                        </dt>
                                        <dd class="text-sm font-medium whitespace-nowrap text-text" v-if="profile.phone">
                                            <span dir="ltr">{{ profile.country_code }}{{ profile.phone }}</span>
                                        </dd>
                                    </dl>
                                </div>
                                <nav class="flex flex-col py-2">
                                    <div class="border-b pb-2 mb-2">
                                        <router-link
                                            v-if="profile.role_id !== enums.roleEnum.CUSTOMER && Object.keys(authDefaultPermission).length > 0"
                                            @click="closeUserDropdown"
                                            class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                            :to="{ name: 'admin.dashboard' }" target="_blank">
                                            <i class="text-sm text-[#A0A3BD] lab-fill-dashboard"></i>
                                            <span class="text-sm font-medium capitalize whitespace-nowrap">
                                                {{ $t('menu.admin_dashboard') }}
                                            </span>
                                        </router-link>
                                    </div>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.overview' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-dashboard"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.user_dashboard') }}
                                        </span>
                                    </router-link>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.orderHistory' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-bag"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.order_history') }}
                                        </span>
                                    </router-link>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.returnOrders' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-refresh"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.return_orders') }}
                                        </span>
                                    </router-link>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.accountInfo' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-user"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.account_info') }}
                                        </span>
                                    </router-link>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.changePassword' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-key"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.change_password') }}
                                        </span>
                                    </router-link>

                                    <router-link
                                        @click="closeUserDropdown"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100"
                                        :to="{ name: 'frontend.account.address' }">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-location"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('menu.address') }}
                                        </span>
                                    </router-link>

                                    <button @click.prevent="logout(); closeUserDropdown();"
                                        class="flex items-center gap-3 px-4 py-2 transition-all duration-500 hover:bg-gray-100">
                                        <i class="text-sm text-[#A0A3BD] lab-fill-logout"></i>
                                        <span class="text-sm font-medium capitalize whitespace-nowrap">
                                            {{ $t('button.logout') }}
                                        </span>
                                    </button>
                                </nav>
                            </div>
                            <div v-else class="p-5">
                                <router-link
                                    @click="closeUserDropdown"
                                    class="bg-gray-100 w-full text-center h-12 leading-12 font-medium text-sm tracking-wide rounded-full whitespace-nowrap mb-3 hover:opacity-80 block"
                                    :to="{ name: 'auth.signup' }">
                                    {{ $t('button.register_your_account') }}
                                </router-link>
                                <router-link
                                    @click="closeUserDropdown"
                                    class="w-full text-center h-12 leading-12 font-semibold tracking-wide rounded-full whitespace-nowrap text-white bg-primary hover:opacity-80 block"
                                    :to="{ name: 'auth.login' }">
                                    {{ $t('button.login_to_your_account') }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                    <!-- My Account End -->

                    <!-- Card Button Start -->
                    <button @click.prevent="openCanvas('cart-canvas')" type="button"
                        class="hidden lg:block flex-shrink-0 relative rounded-md hover:opacity-80 bg-primary">
                        <i class="lab-line-bag text-xl w-10 h-10 !leading-10 text-center text-white"></i>
                        <span v-if="carts.length > 0"
                            class="cart-counter absolute text-[10px] leading-1 text-center rounded-md bg-white text-primary">
                            {{ carts.length }}
                        </span>
                    </button>
                    <!-- Card Button End -->
                     
                </div>

            </div>
        </div>
    </header>

    <div :class="isSticky === true ? 'div-top mb-5 sm:mb-8' : 'hidden'"></div>

    <!-- Mobile Search Start -->
    <form @submit.prevent="search" id="search"
        class="w-full lg:w-auto fixed inset-0 z-[60] pt-4 pb-3 px-4 overflow-x-hidden overflow-y-auto bg-white duration-300 transition-all ltr:-translate-x-full rtl:translate-x-full">
        <div class="flex items-center justify-center mb-4">
            <router-link :to="{ name: 'frontend.home' }"
                class="router-link-active router-link-exact-active flex-shrink-0">
                <img class="mt-1 w-40 sm:w-48" :src="setting.theme_logo" alt="logo">
            </router-link>
            <button type="button" class="absolute right-4" @click.prevent="hideTarget('search', 'search-active')">
                <i class="lab-line-cross text-3xl text-gray-500"></i>
            </button>
        </div>
        <div
            class="w-full h-10 rounded-full flex items-center gap-2 px-4 mb-4 bg-gray-100 transition-all duration-300 focus-within:border-primary focus-within:bg-white border">
            <button class="lab-line-search text-lg flex-shrink-0"></button>
            <input id="searchSomething" v-model="searchProduct" @keyup="searchElement" class="w-full h-full !ring-0 ring-0 focus:ring-0 focus-visible:ring-0 !outline-none outline-none focus:outline-none focus-visible:outline-none"
                type="search" :placeholder="$t('label.search') + '...'">
        </div>
        <div class="lg:hidden h-[calc(100vh_-_140px)] rounded-xl overflow-y-auto p-4">
            <ul v-if="searchProductLists.length > 0" id="searchProductLists">
                <li :key="searchProductList.name"
                    class="py-1 hover:px-2 whitespace-nowrap overflow-hidden text-ellipsis rounded-lg transition-all duration-300 hover:bg-white hover:text-primary"
                    @click.prevent="goSearchProduct(searchProductList.slug)"
                    v-for="searchProductList in searchProductLists">{{ searchProductList.name }}</li>
            </ul>
        </div>
    </form>
    <!-- Mobile Search End -->

    <!-- Notification Start -->
    <div id="order-modal" v-if="orderNotificationStatus" ref="orderNotificationModal" class="modal active ff-modal">
        <div class="modal-dialog max-w-[360px] p-6 text-center relative">
            <button @click.prevent="closeOrderNotificationModal('order-modal', 'modal-active')"
                class="modal-close absolute top-4 right-4">
                <i class="fa-regular fa-circle-xmark"></i>
            </button>
            <h3 class="text-[18px] font-semibold leading-8 mb-6">
                {{ orderNotificationMessage }}
                <span class="block">{{ $t('message.please_check_your_order_list') }}</span>
            </h3>
            <router-link :to="{ path: '/admin/' + orderNotification.url }"
                class="db-btn h-[38px] shadow-[0px_8px_15px_rgba(253,_139,_14,_0.18)] bg-primary text-white">
                {{ $t('button.let_me_check') }}
            </router-link>
        </div>
    </div>
    <!-- Notification End -->

</template>

<script>
import statusEnum from "../../../enums/modules/statusEnum";
import { onMounted, ref } from "vue";
import targetService from "../../../services/targetService";
import appService from "../../../services/appService";
import activityEnum from "../../../enums/modules/activityEnum";
import roleEnum from "../../../enums/modules/roleEnum";
import MenuChildrenComponent from "../../frontend/components/MenuChildrenComponent";
import orderTypeEnum from "../../../enums/modules/orderTypeEnum";
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import _ from "lodash";
import axios from 'axios';
import {useCanvas} from "../../../composables/canvas";


export default {
    name: "FrontendNavbarComponent",
    components: { MenuChildrenComponent },
    setup() {
        const isSticky = ref();
        const {openCanvas} = useCanvas();
        onMounted(() => {
            window.addEventListener('scroll', function () {
                let windowScroll = this.scrollY;
                if (windowScroll > 0) {
                    isSticky.value = true;
                } else {
                    isSticky.value = false;
                }
            })
        })
        return { isSticky, 
                openCanvas
        }
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            searchProductLists: [],
            currentRoute: "",
            defaultLanguage: null,
            enums: {
                activityEnum: activityEnum,
                roleEnum: roleEnum
            },
            languageProps: {
                paginate: 0,
                order_column: "id",
                order_type: "asc",
                status: statusEnum.ACTIVE
            },
            categoryTabStatus: false,
            activeTab: null,
            isCategoryOpen: false,
            searchProduct: "",
            orderNotificationStatus: false,
            orderNotificationMessage: "",
            orderNotification: {
                permission: false,
                url: ""
            },
            categoryMarqueeInterval: null,
            isUserDropdownClosed: false,
        }
    },
    computed: {
        selectedCurrency: function () {
            const globalState = this.$store.getters['globalState/lists'] || {};
            const currencies = this.$store.getters['currency/lists'] || [];
            if (globalState.currency_id && currencies.length > 0) {
                const found = currencies.find(c => Number(c.id) === Number(globalState.currency_id));
                if (found) return found;
            }
            if (globalState.currency_code && currencies.length > 0) {
                const found = currencies.find(c => c.code === globalState.currency_code);
                if (found) return found;
            }
            return currencies.length > 0 ? currencies[0] : null;
        },
        activeCurrencies: function () {
            return this.$store.getters['currency/lists'] || [];
        },
        activeCategory: function () {
            if (!this.categories || this.categories.length === 0) return null;
            if (!this.activeTab) {
                return this.categories[0];
            }
            return this.categories.find(cat => 'category_' + cat.slug === this.activeTab) || this.categories[0];
        },
        logged: function () {
            return this.$store.getters.authStatus;
        },
        authDefaultPermission: function () {
            return this.$store.getters.authDefaultPermission;
        },
        profile: function () {
            return this.$store.getters.authInfo;
        },
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        language: function () {
            return this.$store.getters['frontendLanguage/show'];
        },
        languages: function () {
            return this.$store.getters['frontendLanguage/lists'];
        },
        categories: function () {
            return this.$store.getters['frontendProductCategory/trees'];
        },
        wishlists: function () {
            return this.$store.getters['frontendWishlist/lists'];
        },
        carts: function () {
            return this.$store.getters['frontendCart/lists'];
        },
        isPwaViewed: function () {
            return localStorage.getItem('pwa_viewed') ? true : false;
        }
    },
    mounted() {
        this.currentRoute = this.$route.path;
        document.addEventListener('click', this.handleDocumentClick);
        this.loading.isActive = true;
        this.orderPermissionCheck();
        this.$store.dispatch('frontendSetting/lists').then(res => {
            const siteDefaultLanguage = res.data.data.site_default_language;
            this.defaultLanguage = siteDefaultLanguage;
            const globalState = this.$store.getters['globalState/lists'];
            if (globalState.language_id > 0) {
                this.defaultLanguage = globalState.language_id;
            }

            this.loading.isActive = false;
            this.$store.dispatch('frontendLanguage/lists', this.languageProps).then(lRes => {
                const activeLangs = lRes.data.data || [];
                let validLang = activeLangs.find(l => Number(l.id) === Number(this.defaultLanguage));
                if (!validLang) {
                    validLang = activeLangs.find(l => Number(l.id) === Number(siteDefaultLanguage));
                }
                if (!validLang && activeLangs.length > 0) {
                    validLang = activeLangs[0];
                }

                if (validLang) {
                    this.$store.dispatch('frontendLanguage/show', validLang.id).then(langRes => {
                        this.$i18n.locale = langRes.data.data.code;
                        this.$store.dispatch("globalState/set", {
                            language_id: langRes.data.data.id,
                            language_code: langRes.data.data.code,
                            display_mode: langRes.data.data.display_mode
                        });
                    }).catch();
                }
            }).catch();
            this.$store.dispatch('currency/lists', { status: statusEnum.ACTIVE }).then(cRes => {
                if (cRes.data && cRes.data.data && cRes.data.data.length > 0) {
                    let activeCurr = null;
                    try {
                        const saved = localStorage.getItem('user_selected_currency');
                        if (saved) {
                            const parsed = JSON.parse(saved);
                            activeCurr = cRes.data.data.find(c => Number(c.id) === Number(parsed.id));
                        }
                    } catch (e) {}
                    if (!activeCurr) {
                        const defId = res.data.data.site_default_currency;
                        activeCurr = cRes.data.data.find(c => Number(c.id) === Number(defId)) || cRes.data.data[0];
                    }
                    if (activeCurr) {
                        this.$store.dispatch("globalState/set", {
                            currency_id: activeCurr.id,
                            currency_code: activeCurr.code,
                            currency_symbol: activeCurr.symbol,
                            exchange_rate: activeCurr.exchange_rate,
                        }).catch();
                    }
                }
            }).catch();

            window.setTimeout(() => {
                this.$store.dispatch('frontendCart/initOrderType', { order_type: orderTypeEnum.DELIVERY });

                if (this.$store.getters.authStatus && res.data.data.notification_fcm_api_key && res.data.data.notification_fcm_auth_domain && res.data.data.notification_fcm_project_id && res.data.data.notification_fcm_storage_bucket && res.data.data.notification_fcm_messaging_sender_id && res.data.data.notification_fcm_app_id && res.data.data.notification_fcm_measurement_id) {
                    initializeApp({
                        apiKey: res.data.data.notification_fcm_api_key,
                        authDomain: res.data.data.notification_fcm_auth_domain,
                        projectId: res.data.data.notification_fcm_project_id,
                        storageBucket: res.data.data.notification_fcm_storage_bucket,
                        messagingSenderId: res.data.data.notification_fcm_messaging_sender_id,
                        appId: res.data.data.notification_fcm_app_id,
                        measurementId: res.data.data.notification_fcm_measurement_id
                    });
                    const messaging = getMessaging();

                    Notification.requestPermission().then((permission) => {
                        if (permission === 'granted') {
                            getToken(messaging, { vapidKey: res.data.data.notification_fcm_public_vapid_key }).then((currentToken) => {
                                if (currentToken) {
                                    axios.post('/frontend/device-token/web', { token: currentToken }).then().catch((error) => {
                                        if (error.response.data.message === 'Unauthenticated.') {
                                            this.$store.dispatch('loginDataReset');
                                        }
                                    });
                                }
                            }).catch();
                        }
                    });

                    onMessage(messaging, (payload) => {
                        const notificationTitle = payload.notification.title;
                        const notificationOptions = {
                            body: payload.notification.body,
                            icon: '/images/required/firebase-logo.png'
                        };
                        new Notification(notificationTitle, notificationOptions);

                        if (payload.data.topicName === 'new-order-found' && this.orderNotification.permission) {
                            this.orderNotificationStatus = true;
                            this.orderNotificationMessage = payload.notification.body;
                            const audio = new Audio(res.data.data.notification_audio);
                            audio.play();
                        }
                    });
                }
            }, 3000);

            this.loading.isActive = false;
        }).catch((err) => {
            this.loading.isActive = false;
        });

        this.loading.isActive = true;
        this.$store.dispatch('frontendProductCategory/trees').then(res => {
            this.loading.isActive = false;
        }).catch((err) => {
            this.loading.isActive = false;
        });

        if (this.logged) {
            this.loading.isActive = true;
            this.$store.dispatch("frontendWishlist/lists").then((res) => {
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        }
    },
    watch: {
        $route() {
            this.closeUserDropdown();
        }
    },
    methods: {
        closeUserDropdown: function () {
            this.isUserDropdownClosed = true;
        },
        showTarget: function (id, cClass) {
            targetService.showTarget(id, cClass);
        },
        hideTarget: function (id, cClass) {
            targetService.hideTarget(id, cClass);
        },
        textShortener: function (text, number = 30) {
            return appService.textShortener(text, number);
        },
        checkIsPathAndRoutePathSame(path) {
            if (this.currentRoute === path) {
                return true;
            }
        },
        changeLanguage: function (id, code, mode) {
            this.defaultLanguage = id;
            this.$store.dispatch("globalState/set", {
                language_id: id,
                language_code: code,
                display_mode: mode
            }).then(res => {
                this.$store.dispatch('frontendLanguage/show', id).then(res => {
                    this.$i18n.locale = res.data.data.code;
                }).catch();
            }).catch();
        },
        changeCurrency: function (currency) {
            this.selectedCurrency = currency;
            try {
                localStorage.setItem('user_selected_currency', JSON.stringify(currency));
            } catch (e) {}
            this.$store.dispatch("globalState/set", {
                currency_id: currency.id,
                currency_code: currency.code,
                currency_symbol: currency.symbol,
                exchange_rate: currency.exchange_rate,
            }).catch();
        },
        logout: function () {
            this.$store.dispatch("logout").then(res => {
                this.$store.dispatch("frontendWishlist/reset");
                this.$router.push({ name: "frontend.home" });
            }).catch();
        },
        search: function () {
            if (typeof this.searchProduct !== "undefined" && this.searchProduct !== "") {
                this.$router.push({ name: "frontend.product", query: { all: this.searchProduct } });
                this.searchProduct = "";
                this.hideTarget('search', 'search-active')
            }
        },
        orderPermissionCheck: function () {
            const permissions = this.$store.getters.authPermission;
            if (permissions.length > 0) {
                _.forEach(permissions, (permission) => {
                    if (permission.name === 'online-orders') {
                        if (permission.access === true) {
                            this.orderNotification.permission = true;
                            this.orderNotification.url = permission.url;
                        }
                    }
                });
            }
        },
        closeOrderNotificationModal: function (id, cClass) {
            targetService.hideTarget(id, cClass);
            this.orderNotificationStatus = false;
        },
        searchElement: function () {
            if (this.searchProduct && this.searchProduct.length > 2) {
                let url = `frontend/product`;
                url = url + appService.requestHandler({ all: this.searchProduct });
                axios.get(url).then((res) => {
                    this.searchProductLists = res.data.data;
                }).catch();
            } else {
                this.searchProductLists = [];
            }
        },
        goSearchProduct: function (slug) {
            targetService.hideTarget('search', 'search-active');
            this.$router.push({ name: 'frontend.product.details', params: { slug: slug } })
        },
        scrollCategoryTabs: function (direction) {
            const track = this.$refs.categoryTabsTrack;
            if (!track) {
                return;
            }

            const distance = direction === 'left' ? -220 : 220;
            track.scrollBy({ left: distance, behavior: 'smooth' });
        },
        startMarqueeScroll: function (direction) {
            this.stopMarqueeScroll();

            this.categoryMarqueeInterval = window.setInterval(() => {
                const track = this.$refs.categoryTabsTrack;
                if (!track) {
                    return;
                }

                const delta = direction === 'left' ? -18 : 18;
                track.scrollLeft = track.scrollLeft + delta;
            }, 16);
        },
        stopMarqueeScroll: function () {
            if (this.categoryMarqueeInterval) {
                window.clearInterval(this.categoryMarqueeInterval);
                this.categoryMarqueeInterval = null;
            }
        },
        closePwaModal: function () {
            const modalTarget = this.$refs.pwaModal;
            modalTarget?.classList?.remove("active");
            document.body.style.overflowY = "auto";
            localStorage.setItem('pwa_viewed', true);
        },
        toggleCategoryMenu: function () {
            this.isCategoryOpen = !this.isCategoryOpen;
            if (this.isCategoryOpen && !this.activeTab && this.categories && this.categories.length > 0) {
                this.activeTab = 'category_' + this.categories[0].slug;
            }
        },
        openCategoryMenu: function () {
            this.isCategoryOpen = true;
            if (!this.activeTab && this.categories && this.categories.length > 0) {
                this.activeTab = 'category_' + this.categories[0].slug;
            }
        },
        closeCategoryMenu: function () {
            this.isCategoryOpen = false;
        },
        handleDocumentClick: function (e) {
            if (this.$refs.categoryNavItem && !this.$refs.categoryNavItem.contains(e.target)) {
                this.isCategoryOpen = false;
            }
        },
        selectCategoryTab: function (category) {
            if (this.activeTab === 'category_' + category.slug) {
                this.closeCategoryMenu();
                this.$router.push({ name: 'frontend.product', query: { category: category.slug } });
            } else {
                this.activeTab = 'category_' + category.slug;
            }
        }
    },
    beforeUnmount() {
        this.stopMarqueeScroll();
        document.removeEventListener('click', this.handleDocumentClick);
    },
    watch: {
        $route(to, from) {
            this.currentRoute = to.path;
        },
    }
}
</script>

<style scoped>
.nav-cat-marquee-track {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.nav-cat-marquee-track::-webkit-scrollbar {
    display: none;
}

.nav-cat-fade-btn {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 20;
    width: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 220ms ease;
}

.group:hover .nav-cat-fade-btn {
    opacity: 1;
}

.nav-cat-fade-btn.fade-left {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
}

.nav-cat-fade-btn.fade-right {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
}

.header-cat-dropdown {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(-16px);
    transition: opacity 280ms cubic-bezier(0.16, 1, 0.3, 1), transform 280ms cubic-bezier(0.16, 1, 0.3, 1), visibility 280ms;
}

.header-cat-dropdown.cat-menu-open {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    transform: translateY(0) !important;
}

.nav-dropdown-smooth {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(12px) scale(0.98);
    transition: opacity 280ms cubic-bezier(0.16, 1, 0.3, 1), transform 280ms cubic-bezier(0.16, 1, 0.3, 1), visibility 280ms;
}

.nav-dropdown-smooth.force-closed {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
    transform: translateY(12px) scale(0.98) !important;
}

.group:hover .nav-dropdown-smooth,
.group:focus-within .nav-dropdown-smooth {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    transform: translateY(0) scale(1) !important;
}

:deep(.sub-cat-slide-enter-active) {
    transition: opacity 240ms cubic-bezier(0.16, 1, 0.3, 1), transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
}

:deep(.sub-cat-slide-leave-active) {
    transition: opacity 150ms cubic-bezier(0.4, 0, 1, 1), transform 150ms cubic-bezier(0.4, 0, 1, 1);
}

:deep(.sub-cat-slide-enter-from) {
    opacity: 0;
    transform: translateY(10px) scale(0.995);
}

:deep(.sub-cat-slide-leave-to) {
    opacity: 0;
    transform: translateY(-4px);
}
</style>